import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Pricing-redesign: RedistributionRateBand admin client.
 *
 * Stores leg 2 of pickup pricing: TooEZ DispatchCenter → carrier intake. Per
 * (dispatchCenter × carrier × weight bucket × validFrom).
 */
export interface RedistributionRateBand {
  id: string;
  dispatchCenterId: string;
  dispatchCenter?: { id: string; code: string; name: string };
  carrierId: string;
  carrier?: { id: string; code: string; name: string };
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  // Pricing-merger: smooth-pricing slope. NULL → flat band.
  perKgCents: number | null;
  anchorWeightKg: number | null;
  // Pricing-merger: per-band dim factor (reserved for future per-band escalation)
  dimFactorCm: number | null;
  dimFactorInch: number | null;
  rushHourSurchargeCents?: number | null;
  currencyCode: string;
  validFrom: string;
  validTo?: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRedistributionRateBandInput {
  dispatchCenterId: string;
  carrierId: string;
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  perKgCents?: number | null;
  anchorWeightKg?: number | null;
  dimFactorCm?: number | null;
  dimFactorInch?: number | null;
  rushHourSurchargeCents?: number | null;
  currencyCode?: string;
  validFrom: string;
  validTo?: string | null;
  active?: boolean;
}

export type UpdateRedistributionRateBandInput =
  Partial<CreateRedistributionRateBandInput>;

export function useGetRedistributionRateBands(params?: {
  dispatchCenterId?: string;
  carrierId?: string;
  active?: boolean;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["redistribution-rate-bands", params],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params?.dispatchCenterId)
        q.append("dispatchCenterId", params.dispatchCenterId);
      if (params?.carrierId) q.append("carrierId", params.carrierId);
      if (params?.active !== undefined)
        q.append("active", String(params.active));

      const { data } = await axios.get<{
        success: boolean;
        data: RedistributionRateBand[];
      }>(
        `${API_URL}/api/v2/admin/redistribution-rate-bands${q.toString() ? `?${q.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

export function useCreateRedistributionRateBand(options?: {
  onSuccess?: (data: RedistributionRateBand) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateRedistributionRateBandInput) => {
      const { data } = await axios.post<{
        success: boolean;
        data: RedistributionRateBand;
      }>(`${API_URL}/api/v2/admin/redistribution-rate-bands`, input, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["redistribution-rate-bands"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useUpdateRedistributionRateBand(options?: {
  onSuccess?: (data: RedistributionRateBand) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data: updateData,
    }: {
      id: string;
      data: UpdateRedistributionRateBandInput;
    }) => {
      const { data } = await axios.patch<{
        success: boolean;
        data: RedistributionRateBand;
      }>(
        `${API_URL}/api/v2/admin/redistribution-rate-bands/${id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["redistribution-rate-bands"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeleteRedistributionRateBand(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(
        `${API_URL}/api/v2/admin/redistribution-rate-bands/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["redistribution-rate-bands"],
      });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

/**
 * Pricing-merger Phase 4: atomic-save mutation for the matrix editor.
 */
export interface AtomicSaveRedistBandInput {
  dispatchCenterId: string;
  carrierId: string;
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  perKgCents?: number | null;
  anchorWeightKg?: number | null;
  dimFactorCm?: number | null;
  dimFactorInch?: number | null;
  rushHourSurchargeCents?: number | null;
  currencyCode?: string;
  validTo?: string | null;
  active?: boolean;
}

export interface AtomicSaveRedistRateBandsInput {
  validFrom: string;
  bands: AtomicSaveRedistBandInput[];
  scopes?: {
    dispatchCenterId: string;
    carrierId: string;
  }[];
  intentionalDiscontinuity?: boolean;
}

export interface RedistContinuityViolation {
  upperBandWeightFromKg: number;
  expectedBaseRateCents: number;
  actualBaseRateCents: number;
  diffCents: number;
}

export class RedistBandContinuityViolationError extends Error {
  constructor(
    public violations: RedistContinuityViolation[],
    message: string
  ) {
    super(message);
    this.name = "RedistBandContinuityViolationError";
  }
}

export function useAtomicSaveRedistRateBands(options?: {
  onSuccess?: (data: RedistributionRateBand[]) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: AtomicSaveRedistRateBandsInput
    ): Promise<RedistributionRateBand[]> => {
      try {
        const { data } = await axios.post<{
          success: boolean;
          data: RedistributionRateBand[];
        }>(
          `${API_URL}/api/v2/admin/redistribution-rate-bands/atomic-save`,
          input,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return data.data;
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 400) {
          const body = err.response.data as {
            error?: string;
            details?: {
              message?: string;
              violations?: RedistContinuityViolation[];
            };
          };
          if (body?.error === "BAND_CONTINUITY_VIOLATION") {
            throw new RedistBandContinuityViolationError(
              body.details?.violations ?? [],
              body.details?.message ?? "Band continuity violation"
            );
          }
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: ["redistribution-rate-bands"],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}
